// src/pages/team/TeamWorkspacePage.tsx
// Team interaction hub. Tabs, frequency-ordered: overview (pulse: activity,
// counters, on-duty, schedule, quick actions), tasks, discussions (team-scoped
// chat), documents, projects, resources (linked entities), people (members +
// governance), settings. See docs/ux/team-ia.md.

import { useEffect, useCallback, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useTask, useAuth, useI18n } from '@react/hooks';
import { teamApi } from '@core/api';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';

import { ScopeProvider, useScope } from '@/components/ScopeProvider';
import { WorkspaceLayout, useWorkspace } from '@/layouts/WorkspaceLayout';
import { buildScopeFromParams } from '@/features/scope/logic';
import { TASK_WORKSPACE_CONFIG, buildApiFilters } from '@/features/workspace/logic';
import { FormRow, InlineEditText, SegmentedControl } from '@/components/form';
import {
  TEAM_TABS,
  parseTeamTab,
  getTeamDisplayName,
  getInitials,
  getAvatarColor,
  getStatusMapping,
  teamStatusMap,
  membershipStatusMap,
  teamTypeMap,
  teamVisibilityMap,
} from '@/features/team/logic';
import type { TeamUserWithMembership } from '@core/types/team';


import { entityStore } from '@/stores/EntityStore';
import { entityToUrn } from '@/features/resources/logic';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { JoinRequestDialog } from '@/features/team/JoinRequestDialog';

import { TeamProjectsTab } from './TeamProjectsTab';
import { TeamDocumentsTab } from './TeamDocumentsTab';
import { TeamOverviewTab } from './TeamOverviewTab';
import { TeamDiscussionsTab } from './TeamDiscussionsTab';
import { TeamResourcesTab } from './TeamResourcesTab';

import { ListView } from '../task/views/ListView';
import { BoardView } from '../task/views/BoardView';

import type { Task } from '@core/types/task';

// ---------------------------------------------------------------------------
// Tab icon paths
// ---------------------------------------------------------------------------

const TAB_ICONS: Record<string, string> = {
  overview: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75',
  discussion: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  link: 'M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244',
  task: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  document: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  folder: 'M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z',
  people: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  activity: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
};

// ---------------------------------------------------------------------------
// Inner tasks workspace (needs scope + workspace context)
// ---------------------------------------------------------------------------

const TasksTabInner = observer(() => {
  const taskStore = useTask();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { scope } = useScope();
  const { state } = useWorkspace();

  useEffect(() => {
    const apiFilters = buildApiFilters(state);
    // Use ListTasksByParent for team/project/entity scopes
    if (scope.scopeEntity?.id && (scope.scopeType === 'team' || scope.scopeType === 'project' || scope.scopeType === 'entity')) {
      taskStore.ListTasksByParent(scope.scopeEntity.type || scope.scopeType, scope.scopeEntity.id, {
        pagination: { cursor: '', limit: 200, direction: 'forward' as const },
        sort: [],
        view: 0,
        date_range: {} as any,
        filters: apiFilters as any,
      });
    } else {
      taskStore.ListTasks({
        pagination: { cursor: '', limit: 200, direction: 'forward' as const },
        sort: [],
        view: 0,
        date_range: {} as any,
        filters: apiFilters as any,
      });
    }
  }, [
    state.filters,
    state.sortBy,
    state.sortDirection,
    state.search,
    scope.scopeType,
    scope.scopeEntity?.id,
  ]);

  const handleTaskClick = useCallback(
    (id: string) => navigate(`/tasks/${id}`),
    [navigate],
  );

  const tasks: Task[] = taskStore.items;

  if (taskStore.isLoading && tasks.length === 0) {
    return (
      <div className="space-y-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2.5 animate-pulse">
            <div className="h-4 w-4 rounded bg-muted" />
            <div className="h-4 w-4 rounded bg-muted" />
            <div className="h-4 w-16 rounded bg-muted" />
            <div className="h-4 flex-1 rounded bg-muted" />
            <div className="h-5 w-16 rounded-full bg-muted" />
            <div className="h-6 w-6 rounded-full bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  if (!taskStore.isLoading && tasks.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <svg className="h-7 w-7 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground">{t('task.noItems')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('task.noItemsDescription')}</p>
      </div>
    );
  }

  switch (state.view) {
    case 'board':
      return (
        <BoardView
          tasks={tasks}
          groupBy={state.groupBy}
          density={state.density}
          onTaskClick={handleTaskClick}
          onTaskMove={() => {}}
        />
      );
    case 'list':
    default:
      return (
        <ListView
          tasks={tasks}
          density={state.density}
          groupBy={state.groupBy}
          onTaskClick={handleTaskClick}
        />
      );
  }
});

// ---------------------------------------------------------------------------
// Tasks tab wrapper (provides scope + workspace)
// ---------------------------------------------------------------------------

const TasksTab = observer(({ teamId, teamName }: { teamId: string; teamName: string }) => {
  const teamScope = useMemo(
    () => buildScopeFromParams('team', teamId, teamName, 'team'),
    [teamId, teamName],
  );

  const navigate = useNavigate();
  const handleCreate = useCallback(
    () => navigate(`/tasks/new?parent_id=${teamId}&parent_type=team`),
    [navigate, teamId],
  );

  return (
    <ScopeProvider defaultScope={teamScope}>
      <WorkspaceLayout
        config={{
          ...TASK_WORKSPACE_CONFIG,
          defaultGroupBy: 'project',
        }}
        title="team.tab.tasks"
        createLabel="task.create"
        onCreateClick={handleCreate}
      >
        <TasksTabInner />
      </WorkspaceLayout>
    </ScopeProvider>
  );
});


// ---------------------------------------------------------------------------
// People tab — members table + governance entry points
// ---------------------------------------------------------------------------

/** Governance sub-pages, folded behind the People tab as labelled cards. */
const GOVERNANCE_LINKS: { key: string; path: string; labelKey: string; descKey: string }[] = [
  { key: 'roles', path: 'roles', labelKey: 'team.people.roles', descKey: 'team.people.rolesDesc' },
  { key: 'applications', path: 'applications', labelKey: 'team.people.applications', descKey: 'team.people.applicationsDesc' },
  { key: 'schedules', path: 'schedules', labelKey: 'team.people.schedules', descKey: 'team.people.schedulesDesc' },
  { key: 'delegations', path: 'delegations', labelKey: 'team.people.delegations', descKey: 'team.people.delegationsDesc' },
  { key: 'temporaryRoles', path: 'temporary-roles', labelKey: 'team.people.temporaryRoles', descKey: 'team.people.temporaryRolesDesc' },
  { key: 'ownership', path: 'ownership', labelKey: 'team.people.ownership', descKey: 'team.people.ownershipDesc' },
  { key: 'audit', path: 'audit', labelKey: 'team.people.audit', descKey: 'team.people.auditDesc' },
  { key: 'automation', path: 'automation-log', labelKey: 'team.people.automation', descKey: 'team.people.automationDesc' },
];

function GovernanceGrid({ teamId, t }: { teamId: string; t: (key: string) => string }) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">{t('team.people.governanceTitle')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {GOVERNANCE_LINKS.map((link) => (
          <Link
            key={link.key}
            to={`/teams/${teamId}/${link.path}`}
            className="rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/40 hover:bg-muted/30 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <p className="text-sm font-medium text-foreground">{t(link.labelKey)}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t(link.descKey)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

const MembersTab = observer(({ teamId }: { teamId: string }) => {
  const store = useTeam();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [members, setMembers] = useState<TeamUserWithMembership[]>([]);

  useEffect(() => {
    if (!teamId) return;
    let alive = true;
    store.ListTeamMembers(teamId)
      .then((res) => { if (alive) setMembers(res.users?.users ?? []); })
      .catch(() => { /* store surfaces the error */ });
    return () => { alive = false; };
  }, [teamId, store]);

  const handleInvite = useCallback(
    () => navigate(`/teams/${teamId}/members?tab=invitations`),
    [navigate, teamId],
  );

  const handleViewAll = useCallback(
    () => navigate(`/teams/${teamId}/members`),
    [navigate, teamId],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" size="sm">{members.length}</Badge>
          <span className="text-sm text-muted-foreground">{t('team.tab.members')}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handleViewAll}>
            {t('common.viewAll')}
          </Button>
          <Button size="sm" onClick={handleInvite}>
            <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            {t('team.invite')}
          </Button>
        </div>
      </div>

      {store.isLoading && members.length === 0 ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
              <div className="h-9 w-9 rounded-full bg-muted" />
              <div className="flex-1 space-y-1.5">
                <div className="h-4 w-32 rounded bg-muted" />
                <div className="h-3 w-48 rounded bg-muted" />
              </div>
              <div className="h-5 w-14 rounded-full bg-muted" />
            </div>
          ))}
        </div>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">{t('team.noMembers')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5">
                  {t('team.field.member')}
                </th>
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5 hidden sm:table-cell">
                  {t('common.status')}
                </th>
                <th className="text-left font-medium text-muted-foreground px-4 py-2.5 hidden md:table-cell">
                  {t('team.field.role')}
                </th>
                <th className="text-right font-medium text-muted-foreground px-4 py-2.5">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {members.slice(0, 20).map((member, index) => {
                const memberId = member.membership?.entity?.id || member.user?.entity?.id || String(index);
                const displayName = member.user?.display_name || '--';
                const statusMapping = getStatusMapping(membershipStatusMap, member.membership?.status ?? '');
                const isOwner = member.is_owner;
                const isAdmin = member.is_admin;

                return (
                  <tr
                    key={memberId}
                    className="border-b border-border/50 last:border-b-0 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                          style={{ backgroundColor: getAvatarColor(displayName) }}
                        >
                          {getInitials(displayName)}
                        </div>
                        <span className="font-medium text-foreground truncate">{displayName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 hidden sm:table-cell">
                      <Badge variant={statusMapping.variant as never} size="sm">{t(statusMapping.labelKey)}</Badge>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      {isOwner && <Badge variant="warning" size="sm">{t('team.field.owner')}</Badge>}
                      {isAdmin && !isOwner && <Badge variant="primary" size="sm">Admin</Badge>}
                      {!isOwner && !isAdmin && <Badge variant="secondary" size="sm">{t('team.field.member')}</Badge>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={`/teams/${teamId}/members/${memberId}`}
                        className="text-xs text-primary hover:underline"
                      >
                        {t('common.view')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {members.length > 20 && (
            <div className="px-4 py-2.5 text-center border-t border-border/50">
              <button
                type="button"
                onClick={handleViewAll}
                className="text-xs text-primary hover:underline"
              >
                {t('common.viewAll')} ({members.length})
              </button>
            </div>
          )}
        </div>
      )}

      <GovernanceGrid teamId={teamId} t={t} />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Settings tab (placeholder form)
// ---------------------------------------------------------------------------

const TEAM_TYPE_SEGMENTS = Object.keys(teamTypeMap).map((val) => ({ value: val, labelKey: teamTypeMap[val].labelKey }));
const TEAM_VISIBILITY_SEGMENTS = Object.keys(teamVisibilityMap).map((val) => ({ value: val, labelKey: teamVisibilityMap[val].labelKey }));

/**
 * General settings — label-left autosave rows (docs/ux/14-forms.md §3),
 * replacing the old routed /teams/:id/edit page. Every commit sends the
 * complete team payload (no update mask on the wire), same as the legacy
 * edit form did.
 */
const SETTINGS_LINKS: { key: string; path: string; labelKey: string; descKey: string }[] = [
  { key: 'full', path: 'settings', labelKey: 'team.nav.settings', descKey: 'team.nav.settingsDesc' },
  { key: 'branding', path: 'branding', labelKey: 'team.nav.branding', descKey: 'team.nav.brandingDesc' },
  { key: 'personal', path: 'my-settings', labelKey: 'team.nav.mySettings', descKey: 'team.nav.mySettingsDesc' },
];

function SettingsTab({ teamId, team, t }: { teamId: string; team: any; t: (key: string) => string }) {
  const store = useTeam();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'leave' | 'delete' | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const handleLeave = async () => {
    const userId = (user as any)?.entity?.id;
    if (!userId) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const res = await teamApi.ListTeamMembers(teamId);
      const mine = (res.users?.users ?? []).find(
        (m) => m.membership?.profile?.id === userId || m.user?.entity?.id === userId,
      );
      const memberId = mine?.membership?.entity?.id;
      if (!memberId) throw new Error(t('team.settings.leaveNotMember'));
      await store.RemoveTeamMember(teamId, memberId);
      navigate('/teams');
    } catch (err) {
      setActionError(err);
      setConfirmAction(null);
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    setActionBusy(true);
    setActionError(null);
    try {
      await store.DeleteTeam(teamId);
      navigate('/teams');
    } catch (err) {
      setActionError(err);
      setConfirmAction(null);
    } finally {
      setActionBusy(false);
    }
  };

  const flashSaved = (field: string) => {
    setSaved(field);
    setTimeout(() => setSaved((cur) => (cur === field ? null : cur)), 2000);
  };

  const commit = async (patch: Record<string, unknown>, field: string) => {
    try {
      await store.UpdateTeam(teamId, {
        name: team?.name ?? '',
        description: team?.description ?? '',
        team_type: team?.team_type ?? 'TEAM_TYPE_PROJECT',
        visibility: team?.visibility ?? 'TEAM_VISIBILITY_PRIVATE',
        status: team?.status ?? 'TEAM_STATUS_ACTIVE',
        max_members: team?.max_members ?? 0,
        ...patch,
      } as never);
      await store.GetTeam(teamId);
      flashSaved(field);
    } catch { /* store surfaces the error */ }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {actionError != null && (
        <ErrorAlert error={actionError} dismissible onDismiss={() => setActionError(null)} />
      )}

      {/* Deeper settings pages */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {SETTINGS_LINKS.map((link) => (
          <Link
            key={link.key}
            to={`/teams/${teamId}/${link.path}`}
            className="rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/40 hover:bg-muted/30 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <p className="text-sm font-medium text-foreground">{t(link.labelKey)}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t(link.descKey)}</p>
          </Link>
        ))}
      </div>

      {/* General */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('team.settings.general')}</CardTitle>
          <span
            role="status"
            className={`text-xs text-muted-foreground transition-opacity ${saved ? 'opacity-100' : 'opacity-0'}`}
          >
            {t('common.saved')}
          </span>
        </CardHeader>
        <CardContent className="divide-y divide-border/60">
          <FormRow label={t('team.field.name')} htmlFor="team-settings-name">
            <InlineEditText
              value={team?.name ?? ''}
              onCommit={(name) => commit({ name }, 'name')}
              placeholder={t('team.field.namePlaceholder')}
              aria-label={t('team.field.name')}
            />
          </FormRow>
          <FormRow label={t('team.field.description')} htmlFor="team-settings-description">
            <InlineEditText
              value={team?.description ?? ''}
              onCommit={(description) => commit({ description }, 'description')}
              placeholder={t('team.field.descriptionPlaceholder')}
              allowEmpty
              aria-label={t('team.field.description')}
            />
          </FormRow>
          <FormRow label={t('team.field.type')}>
            <SegmentedControl
              aria-label={t('team.field.type')}
              size="sm"
              value={team?.team_type ?? 'TEAM_TYPE_PROJECT'}
              onChange={(team_type) => commit({ team_type }, 'type')}
              options={TEAM_TYPE_SEGMENTS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </FormRow>
          <FormRow label={t('team.field.visibility')}>
            <SegmentedControl
              aria-label={t('team.field.visibility')}
              size="sm"
              value={team?.visibility ?? 'TEAM_VISIBILITY_PRIVATE'}
              onChange={(visibility) => commit({ visibility }, 'visibility')}
              options={TEAM_VISIBILITY_SEGMENTS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </FormRow>
          <FormRow label={t('team.field.maxMembers')} hint={t('team.field.maxMembersHint')} htmlFor="team-settings-max-members">
            <input
              id="team-settings-max-members"
              type="text"
              inputMode="numeric"
              defaultValue={team?.max_members ? String(team.max_members) : ''}
              placeholder={t('team.field.maxMembersUnlimited')}
              onBlur={(e) => {
                const v = e.target.value;
                if (v !== '' && !/^\d+$/.test(v)) return;
                commit({ max_members: v === '' ? 0 : parseInt(v, 10) }, 'maxMembers');
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-destructive">{t('team.settings.dangerZone')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{t('team.settings.leaveTeam')}</p>
              <p className="text-xs text-muted-foreground">{t('team.settings.leaveTeamDescription')}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setConfirmAction('leave')}>
              {t('team.settings.leaveTeam')}
            </Button>
          </div>
          <div className="border-t border-border/50 pt-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{t('team.settings.deleteTeam')}</p>
              <p className="text-xs text-muted-foreground">{t('team.settings.deleteTeamDescription')}</p>
            </div>
            <Button variant="danger" size="sm" onClick={() => setConfirmAction('delete')}>
              {t('common.delete')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction === 'delete' ? t('team.settings.deleteTeam') : t('team.settings.leaveTeam')}
        description={
          confirmAction === 'delete'
            ? t('team.settings.deleteTeamDescription')
            : t('team.settings.leaveTeamDescription')
        }
        destructive
        isConfirming={actionBusy}
        onConfirm={confirmAction === 'delete' ? handleDelete : handleLeave}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-4 w-32 rounded bg-muted animate-pulse" />
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-xl bg-muted animate-pulse" />
        <div className="space-y-2">
          <div className="h-6 w-48 rounded bg-muted animate-pulse" />
          <div className="h-4 w-64 rounded bg-muted animate-pulse" />
        </div>
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-9 w-24 rounded-md bg-muted animate-pulse" />
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 rounded bg-muted animate-pulse" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export const TeamWorkspacePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const store = useTeam();
  const { user } = useAuth();
  const { t } = useI18n();
  const [isMember, setIsMember] = useState<boolean | null>(null);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [joinSent, setJoinSent] = useState(false);

  const activeTab = parseTeamTab(searchParams.get('tab'));
  const team = store.currentItem as any;

  // Load team
  useEffect(() => {
    if (id) store.GetTeam(id);
    return () => { store.clearCurrent(); entityStore.clearContext(); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Membership check drives the "request to join" affordance for visitors.
  useEffect(() => {
    const userId = (user as any)?.entity?.id;
    if (!id || !userId) return;
    teamApi.ListTeamMembers(id).then((res) => {
      setIsMember((res.users?.users ?? []).some(
        (m) => m.membership?.profile?.id === userId || m.user?.entity?.id === userId,
      ));
    }).catch(() => setIsMember(null));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Give the right rail (resources / aspects) the team as its entity context.
  // Team responses carry no `urn` field — compose it from the entity's parts.
  const teamUrn = team?.entity ? entityToUrn(team.entity) : '';
  useEffect(() => {
    if (id && team?.entity) {
      entityStore.loadEntityData({
        urn: teamUrn,
        type: 'team',
        id,
        title: getTeamDisplayName(team),
      });
    }
  }, [id, teamUrn]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = useCallback(
    (tab: string, extra?: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      next.delete('new');
      if (extra) {
        for (const [k, v] of Object.entries(extra)) next.set(k, v);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const discussionsCreateRequested = searchParams.get('new') === '1';
  const clearCreateRequest = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const teamName = getTeamDisplayName(team) || t('team.title');

  // Loading
  if ((store.isLoading || store.isLoadingItem) && !team) {
    return <PageSkeleton />;
  }

  // Error
  if (store.error && !team) {
    return (
      <div className="space-y-4">
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
        <Link to="/teams" className="text-sm text-primary hover:underline">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  // Not found
  if (!store.isLoading && !store.isLoadingItem && !team) {
    return (
      <div className="space-y-4">
        <Alert variant="warning" title={t('common.notFound')}>
          {t('team.notFound')}
        </Alert>
        <Link to="/teams" className="text-sm text-primary hover:underline">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  const status = team ? getStatusMapping(teamStatusMap, team.status) : null;
  const memberCount = team?.members_count ?? team?.counters?.members_count ?? 0;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground transition-colors">
          {t('team.list')}
        </Link>
        <svg className="h-4 w-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-foreground font-medium truncate">{teamName}</span>
      </nav>

      {/* Team header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Avatar */}
        <div
          className="flex-shrink-0 h-14 w-14 rounded-xl flex items-center justify-center text-white text-lg font-bold shadow-sm"
          style={{ backgroundColor: getAvatarColor(teamName) }}
        >
          {getInitials(teamName)}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-foreground truncate">{teamName}</h1>
            {status && (
              <Badge variant={status.variant as any} size="sm">{t(status.labelKey)}</Badge>
            )}
            {memberCount > 0 && (
              <Badge variant="secondary" size="sm">
                <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                {memberCount}
              </Badge>
            )}
          </div>
          {team?.description && (
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{team.description}</p>
          )}
        </div>

        {/* Header actions */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {isMember === false && team?.visibility !== 'TEAM_VISIBILITY_SECRET' && !joinSent && (
            <Button size="sm" onClick={() => setShowJoinDialog(true)}>
              {t('team.joinRequest.button')}
            </Button>
          )}
          <Link to={`/teams/${id}/settings`}>
            <Button variant="secondary" size="sm">
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICONS.settings} />
              </svg>
              {t('team.settingsLabel')}
            </Button>
          </Link>
        </div>
      </div>

      {joinSent && (
        <Alert variant="success" dismissible onDismiss={() => setJoinSent(false)}>
          {t('team.joinRequest.sent')}
        </Alert>
      )}

      {showJoinDialog && id && (
        <JoinRequestDialog
          teamId={id}
          teamName={teamName}
          onClose={() => setShowJoinDialog(false)}
          onSuccess={() => { setShowJoinDialog(false); setJoinSent(true); }}
        />
      )}

      {/* Tab bar */}
      <div className="border-b border-border -mx-1">
        <div className="flex gap-0 overflow-x-auto scrollbar-none px-1" role="tablist" aria-label={t('team.workspace.tabs')}>
          {TEAM_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d={TAB_ICONS[tab.icon] || TAB_ICONS.task} />
                </svg>
                <span className="hidden sm:inline">{t(tab.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div role="tabpanel">
        {activeTab === 'overview' && id && (
          <TeamOverviewTab teamId={id} team={team} onNavigateTab={handleTabChange} />
        )}
        {activeTab === 'tasks' && id && <TasksTab teamId={id} teamName={teamName} />}
        {activeTab === 'discussions' && id && (
          <TeamDiscussionsTab
            teamId={id}
            initialCreateOpen={discussionsCreateRequested}
            onCreateConsumed={clearCreateRequest}
          />
        )}
        {activeTab === 'documents' && id && <TeamDocumentsTab teamId={id} />}
        {activeTab === 'projects' && id && <TeamProjectsTab teamId={id} />}
        {activeTab === 'resources' && id && (
          <TeamResourcesTab teamId={id} teamUrn={teamUrn} teamName={teamName} />
        )}
        {activeTab === 'people' && id && <MembersTab teamId={id} />}
        {activeTab === 'settings' && id && <SettingsTab teamId={id} team={team} t={t} />}
      </div>
    </div>
  );
});

export default TeamWorkspacePage;
